import { Fragment, useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { useIsMobile, useSortable, SortTh } from '../utils';

const ACTION_COLOR = {
  VIEW_RECOVERY_KEY:     'var(--yellow)',
  DOWNLOAD_RECOVERY_KIT: 'var(--red)',
  // #204 — las acciones que además notifican a los admins (AUDITED_ACTIONS).
  SETTINGS_CREDENTIALS_UPDATED: 'var(--red)',
  OPERATOR_PERMISSIONS_CHANGED: 'var(--red)',
  RESELLER_PERMISSIONS_CHANGED: 'var(--red)',
  MFA_DISABLED:                 'var(--red)',
  USER_EMAIL_CHANGED:           'var(--red)',
  USER_PASSWORD_SET:            'var(--red)',
  // #204 tanda 2 — 403: cada rechazo en amarillo, el tope alcanzado (notifica) en rojo.
  ACCESS_DENIED:                'var(--yellow)',
  ACCESS_DENIED_LIMIT_REACHED:  'var(--red)',
};

const EMPTY_FILTERS = { action: '', userEmail: '', ip: '', dateFrom: '', dateTo: '' };

function formatDT(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('es-AR', { hour12: false });
}

function csvEscape(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildCsv(entries) {
  const header = ['Fecha', 'Usuario', 'Accion', 'Objetivo', 'IP', 'Detalle'];
  const rows = entries.map(e => [formatDT(e.created_at), e.user_email || '', e.action, e.target || '', e.ip || '', e.detail || '']);
  return [header, ...rows].map(row => row.map(csvEscape).join(',')).join('\n');
}

function downloadCsv(entries) {
  const csv = buildCsv(entries);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AuditLog() {
  const isMobile              = useIsMobile();
  const [entries, setEntries] = useState([]);
  const [limit, setLimit]     = useState(50);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [draft, setDraft]     = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = id => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const intervalRef           = useRef(null);
  const appliedRef            = useRef(applied);
  appliedRef.current = applied;

  const hasActiveFilters = Object.values(applied).some(v => v);

  const load = async () => {
    try {
      const f = appliedRef.current;
      const data = await api.getAuditLog({
        limit,
        action:    f.action,
        userEmail: f.userEmail,
        ip:        f.ip,
        dateFrom:  f.dateFrom,
        dateTo:    f.dateTo ? `${f.dateTo}T23:59:59` : '',
      });
      setEntries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.getAuditLogActions().then(setActions).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30000);
    return () => clearInterval(intervalRef.current);
  }, [limit, applied]); // eslint-disable-line

  const applyFilters  = () => setApplied(draft);
  const clearFilters  = () => { setDraft(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); };

  const { sorted, sortKey, sortDir, toggle } = useSortable(entries, 'created_at', 'desc');

  const inpStyle = { background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 10px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const lblStyle = { display: 'block', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4, fontFamily: 'var(--mono)' };

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-end', justifyContent: 'space-between', marginBottom: 24, gap: isMobile ? 12 : 0 }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>Auditoría</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>Accesos a claves y kits de recuperación</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>Mostrar:</span>
          {[50, 100, 200].map(n => (
            <button key={n} onClick={() => setLimit(n)}
              style={{ background: limit === n ? 'var(--accent)' : 'transparent', border: '1px solid var(--border)', color: limit === n ? 'var(--bg)' : 'var(--muted)', padding: '5px 12px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', fontWeight: limit === n ? 700 : 400 }}>
              {n}
            </button>
          ))}
          <button onClick={() => downloadCsv(sorted)} disabled={sorted.length === 0}
            style={{ background: 'transparent', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '5px 12px', fontSize: 11, cursor: sorted.length === 0 ? 'default' : 'pointer', fontFamily: 'var(--mono)', opacity: sorted.length === 0 ? 0.4 : 1 }}>
            ⬇ EXPORTAR CSV
          </button>
        </div>
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 16 }}>
        <div onClick={() => isMobile && setFiltersOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', cursor: isMobile ? 'pointer' : 'default', borderBottom: filtersOpen ? '1px solid var(--border)' : 'none' }}>
          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted)' }}>
            Filtros{hasActiveFilters ? ` — ${Object.values(applied).filter(Boolean).length} activos` : ''}
          </span>
          {isMobile && <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{filtersOpen ? '▾' : '▸'}</span>}
        </div>
        {filtersOpen && (
          <div style={{ padding: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={lblStyle}>Acción</label>
                <select value={draft.action} onChange={e => setDraft(d => ({ ...d, action: e.target.value }))} style={{ ...inpStyle, cursor: 'pointer' }}>
                  <option value="">Todas</option>
                  {actions.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label style={lblStyle}>Usuario</label>
                <input value={draft.userEmail} onChange={e => setDraft(d => ({ ...d, userEmail: e.target.value }))} placeholder="email..." style={inpStyle} />
              </div>
              <div>
                <label style={lblStyle}>IP</label>
                <input value={draft.ip} onChange={e => setDraft(d => ({ ...d, ip: e.target.value }))} placeholder="10.0.0.1" style={inpStyle} />
              </div>
              <div>
                <label style={lblStyle}>Desde</label>
                <input type="date" value={draft.dateFrom} onChange={e => setDraft(d => ({ ...d, dateFrom: e.target.value }))} style={inpStyle} />
              </div>
              <div>
                <label style={lblStyle}>Hasta</label>
                <input type="date" value={draft.dateTo} onChange={e => setDraft(d => ({ ...d, dateTo: e.target.value }))} style={inpStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={applyFilters} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 14px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', fontWeight: 700 }}>FILTRAR</button>
              <button onClick={clearFilters} disabled={!hasActiveFilters} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '7px 14px', fontSize: 11, cursor: hasActiveFilters ? 'pointer' : 'default', fontFamily: 'var(--mono)', opacity: hasActiveFilters ? 1 : 0.4 }}>LIMPIAR FILTROS</button>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>
      ) : entries.length === 0 ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
          {hasActiveFilters ? 'Sin registros de auditoría que coincidan con los filtros aplicados.' : 'Sin registros de auditoría todavía.'}
        </div>
      ) : isMobile ? (
        <div>
          {sorted.map(e => (
            <div key={e.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>FECHA</div>
              <div style={{ fontSize: 13, marginBottom: 10, fontFamily: 'var(--mono)', color: 'var(--muted)' }}>{formatDT(e.created_at)}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>USUARIO</div>
              <div style={{ fontSize: 13, marginBottom: 10 }}>{e.user_email || '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>ACCIÓN</div>
              <div style={{ fontSize: 13, marginBottom: 10 }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: ACTION_COLOR[e.action] || 'var(--muted)', letterSpacing: 1 }}>{e.action}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>OBJETIVO</div>
              <div style={{ fontSize: 13, wordBreak: 'break-word' }}>{e.target || '—'}</div>
              {e.detail && (
                <div style={{ marginTop: 10 }}>
                  <div onClick={() => toggleExpanded(e.id)} style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', cursor: 'pointer' }}>
                    {expanded.has(e.id) ? '▾' : '▸'} DETALLE
                  </div>
                  {expanded.has(e.id) && (
                    <div style={{ fontSize: 12, marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text)' }}>{e.detail}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg)' }}>
                {[
                  ['Fecha',    'created_at'],
                  ['Usuario',  'user_email'],
                  ['Acción',   'action'],
                  ['Objetivo', 'target'],
                  ['IP',       'ip'],
                ].map(([label, col]) => (
                  <SortTh key={col} label={label} colKey={col} sortKey={sortKey} sortDir={sortDir} onToggle={toggle}
                    style={{ padding: '10px 14px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((e, i) => (
                <Fragment key={e.id}>
                <tr onClick={e.detail ? () => toggleExpanded(e.id) : undefined}
                  style={{ borderBottom: i < sorted.length - 1 && !expanded.has(e.id) ? '1px solid var(--border)' : 'none', cursor: e.detail ? 'pointer' : 'default' }}>
                  <td style={{ padding: '10px 14px', fontFamily: 'var(--mono)', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-block', width: 14 }}>{e.detail ? (expanded.has(e.id) ? '▾' : '▸') : ''}</span>
                    {formatDT(e.created_at)}
                  </td>
                  <td style={{ padding: '10px 14px', fontFamily: 'var(--mono)' }}>{e.user_email || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: ACTION_COLOR[e.action] || 'var(--muted)', letterSpacing: 1 }}>{e.action}</span>
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text)' }}>{e.target || '—'}</td>
                  <td style={{ padding: '10px 14px', fontFamily: 'var(--mono)', color: 'var(--muted)', fontSize: 11 }}>{e.ip || '—'}</td>
                </tr>
                {e.detail && expanded.has(e.id) && (
                  <tr style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <td colSpan={5} style={{ padding: '0 14px 12px 28px', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text)' }}>{e.detail}</td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
